import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getUserByAuthId } from "../Repository/Api";
import { getFirebaseAuthModule } from "../Repository/Authentication/Firebase";
import { browserLocalPersistence, setPersistence } from "firebase/auth";

const AUTH_PROFILE_CACHE_KEY = "AUTH_PROFILE_CACHE_V1";
const AUTH_PROFILE_TTL_MS = 15 * 60 * 1000;
/** Persisted for sponsors; used as FilterId on game video lists (coach’s cami codes). */
export const SPONSOR_COACH_ID_STORAGE_KEY = "sponsorCoachId";

const AuthUserContext = createContext({
  authReady: false,
  profileReady: false,
  profileLoading: false,
  isLoggedIn: false,
  profile: null,
  userTypeId: null,
  isCoach: false,
  isSponsor: false,
  refreshProfile: async () => null,
});

function readProfileCache() {
  try {
    const raw = localStorage.getItem(AUTH_PROFILE_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
}

function isFreshProfile(profile) {
  if (!profile || !profile.updatedAt) {
    return false;
  }
  return Date.now() - profile.updatedAt < AUTH_PROFILE_TTL_MS;
}

function mapProfile(response, authId) {
  return {
    authId,
    userTypeId: response?.userTypeId ?? null,
    /** Backend user id; used as FilterId in list APIs. */
    id: response?.id ?? response?.userId ?? null,
    /** Linked coach user id when present. */
    coachId: response?.coachId ?? null,
    email: response?.email ?? "",
    firstName: response?.firstName ?? "",
    lastName: response?.lastName ?? "",
    freeCamiCodes: response?.freeCamiCodes ?? 0,
    foreverCamiCodes: response?.foreverCamiCodes ?? 0,
    userPhoto: response?.userPhoto ?? "",
    updatedAt: Date.now(),
  };
}

function writeProfileCache(profile) {
  try {
    localStorage.setItem(AUTH_PROFILE_CACHE_KEY, JSON.stringify(profile));
  } catch (error) {
    // ignore cache write failures
  }
}

export function clearAuthProfileCache() {
  try {
    localStorage.removeItem(AUTH_PROFILE_CACHE_KEY);
    localStorage.removeItem(SPONSOR_COACH_ID_STORAGE_KEY);
  } catch (error) {
    // ignore cache clear failures
  }
}

export function AuthUserProvider({ children }) {
  const [profile, setProfile] = useState(() => readProfileCache());
  const [authReady, setAuthReady] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const loadProfile = useCallback(async (authId, force = false) => {
    if (!authId) {
      return null;
    }

    const cachedProfile = readProfileCache();
    if (!force && cachedProfile?.authId === authId && isFreshProfile(cachedProfile)) {
      setProfile(cachedProfile);
      return cachedProfile;
    }

    const response = await getUserByAuthId(authId);
    const nextProfile = mapProfile(response, authId);
    if (nextProfile.userTypeId === 6 && nextProfile.coachId != null) {
      try {
        localStorage.setItem(SPONSOR_COACH_ID_STORAGE_KEY, String(nextProfile.coachId));
      } catch {
        /* ignore */
      }
    }
    setProfile(nextProfile);
    writeProfileCache(nextProfile);
    return nextProfile;
  }, []);

  useEffect(() => {
    const auth = getFirebaseAuthModule();
    setPersistence(auth, browserLocalPersistence).catch(() => {});
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        setIsLoggedIn(false);
        setAuthReady(true);
        setProfileLoading(false);
        setProfile(null);
        clearAuthProfileCache();
        return;
      }

      setIsLoggedIn(true);
      user
        .getIdToken()
        .then((newToken) => {
          localStorage.setItem("JWTTOKEN", newToken);
        })
        .catch(() => {});

      setAuthReady(true);
      setProfileLoading(true);
      loadProfile(user.uid, false)
        .catch(() => {
          setProfile(null);
          clearAuthProfileCache();
        })
        .finally(() => setProfileLoading(false));
    });

    return () => unsubscribe();
  }, [loadProfile]);

  const profileReady = !isLoggedIn || !!profile || !profileLoading;
  const userTypeId = profile?.userTypeId ?? null;

  const value = useMemo(
    () => ({
      authReady,
      profileReady,
      profileLoading,
      isLoggedIn,
      profile,
      userTypeId,
      isCoach: userTypeId === 5,
      isSponsor: userTypeId === 6,
      refreshProfile: async () => {
        const auth = getFirebaseAuthModule();
        const authId = auth?.currentUser?.uid;
        if (!authId) {
          setProfileLoading(false);
          return null;
        }
        setProfileLoading(true);
        try {
          return await loadProfile(authId, true);
        } catch {
          return null;
        } finally {
          setProfileLoading(false);
        }
      },
    }),
    [authReady, profileReady, profileLoading, isLoggedIn, profile, userTypeId, loadProfile]
  );

  return <AuthUserContext.Provider value={value}>{children}</AuthUserContext.Provider>;
}

export function useAuthUser() {
  return useContext(AuthUserContext);
}
